import { Link, router } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { EditorRenderer } from '@/components/Editor';

/**
 * Stage data from the backend.
 */
interface Stage {
    id: number;
    stage_number: number;
    dialogue_pre: string | null;
    dialogue_success: string | null;
    dialogue_failure: string | null;
    challenges: {
        id: number;
        stat_type: string;
        difficulty: number;
    }[];
    rewards: {
        id: number;
        reward_type: { value: string; label: string; icon: string };
        item_recipe: { id: number; name: string } | null;
        item_rarity: { value: string; label: string } | null;
        material_type: string | null;
        enclave: { id: number; name: string; owner_name: string | null } | null;
        reputation_min: number | null;
        reputation_max: number | null;
        quantity_min: number;
        quantity_max: number;
        chance_percent: number;
        tags: { id: number; name: string }[];
    }[];
}

/**
 * Adventure data from the backend.
 */
interface Adventure {
    id: number;
    name: string;
    slug: string;
    description: string | null;
    dialogue_start: string | null;
    dialogue_success: string | null;
    dialogue_failure: string | null;
    is_active: boolean;
    is_multi_stage: boolean;
    stage_count: number;
    stages?: Stage[];
    attempts_count: number;
    created_by: string | null;
    created_at: string;
    challenges: {
        id: number;
        stat_type: string;
        difficulty: number;
    }[];
    rewards: {
        id: number;
        reward_type: { value: string; label: string; icon: string };
        item_recipe: { id: number; name: string } | null;
        item_rarity: { value: string; label: string } | null;
        material_type: string | null;
        enclave: { id: number; name: string; owner_name: string | null } | null;
        reputation_min: number | null;
        reputation_max: number | null;
        quantity_min: number;
        quantity_max: number;
        chance_percent: number;
        tags: { id: number; name: string }[];
    }[];
}

/**
 * Props for the Show page.
 */
interface Props {
    adventure: Adventure;
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}

/**
 * GM Adventures Show page - displays adventure details.
 */
export default function Show({ adventure, statLabels, skillLabels }: Props) {
    // Combine stat and skill labels for display
    const allLabels = { ...statLabels, ...skillLabels };

    // Handle delete
    const handleDelete = () => {
        if (confirm(`Are you sure you want to delete "${adventure.name}"?`)) {
            router.delete(`/gm/adventures/${adventure.id}`);
        }
    };

    return (
        <GmLayout title={adventure.name} activePath="/gm/adventures">
            <div className="max-w-4xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-white">{adventure.name}</h1>
                        <p className="text-gray-400 text-sm">Slug: {adventure.slug}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link
                            href="/gm/adventures"
                            className="text-gray-400 hover:text-white transition-colors"
                        >
                            ← Back
                        </Link>
                        <Link
                            href={`/gm/adventures/${adventure.id}/edit`}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                        >
                            Edit
                        </Link>
                        <button
                            onClick={handleDelete}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                        >
                            Delete
                        </button>
                    </div>
                </div>

                {/* Status Badge */}
                <div className="mb-6 flex items-center gap-3 flex-wrap">
                    <span className={`px-3 py-1 rounded text-sm ${adventure.is_active ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}>
                        {adventure.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {adventure.is_multi_stage && (
                        <span className="px-3 py-1 rounded text-sm bg-purple-600 text-white">
                            Multi-Stage ({adventure.stage_count} stages)
                        </span>
                    )}
                    <span className="text-gray-400 text-sm">
                        {adventure.attempts_count} attempt{adventure.attempts_count !== 1 ? 's' : ''}
                    </span>
                </div>

                {/* Description - Rendered as Markdown */}
                {adventure.description && (
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
                        <h2 className="text-lg font-semibold text-white mb-2">Description</h2>
                        <EditorRenderer content={adventure.description} />
                    </div>
                )}

                {/* Single-stage: Dialogue, Challenges, Rewards */}
                {!adventure.is_multi_stage && (
                <>
                {/* Dialogue - Rendered as Markdown */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
                    <h2 className="text-lg font-semibold text-white mb-4">Dialogue</h2>
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-sm font-medium text-gray-400 mb-1">Start</h3>
                            {adventure.dialogue_start ? (
                                <EditorRenderer content={adventure.dialogue_start} />
                            ) : (
                                <p className="text-gray-500 italic">No start dialogue</p>
                            )}
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-green-400 mb-1">Success</h3>
                            {adventure.dialogue_success ? (
                                <EditorRenderer content={adventure.dialogue_success} />
                            ) : (
                                <p className="text-gray-500 italic">No success dialogue</p>
                            )}
                        </div>
                        <div>
                            <h3 className="text-sm font-medium text-red-400 mb-1">Failure</h3>
                            {adventure.dialogue_failure ? (
                                <EditorRenderer content={adventure.dialogue_failure} />
                            ) : (
                                <p className="text-gray-500 italic">No failure dialogue</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Challenges */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
                    <h2 className="text-lg font-semibold text-white mb-4">Challenges ({adventure.challenges.length})</h2>
                    {adventure.challenges.length === 0 ? (
                        <p className="text-gray-400">No challenges defined.</p>
                    ) : (
                        <div className="space-y-2">
                            {adventure.challenges.map((challenge) => (
                                <div key={challenge.id} className="flex items-center gap-4 p-3 bg-slate-700 rounded">
                                    <span className="text-white font-medium">{allLabels[challenge.stat_type] || challenge.stat_type}</span>
                                    <span className="text-gray-400">Difficulty: {challenge.difficulty}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Rewards */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
                    <h2 className="text-lg font-semibold text-white mb-4">Rewards ({adventure.rewards.length})</h2>
                    {adventure.rewards.length === 0 ? (
                        <p className="text-gray-400">No rewards defined.</p>
                    ) : (
                        <div className="space-y-3">
                            {adventure.rewards.map((reward) => (
                                <RewardDisplay key={reward.id} reward={reward} />
                            ))}
                        </div>
                    )}
                </div>
                </>
                )}

                {/* Multi-stage: Stages */}
                {adventure.is_multi_stage && adventure.stages && (
                <div className="space-y-6">
                    {adventure.stages.map((stage) => (
                        <div key={stage.id} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                            {/* Stage Header */}
                            <div className="bg-slate-700/50 px-6 py-3 border-b border-slate-700">
                                <h2 className="text-lg font-semibold text-white flex items-center gap-3">
                                    <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded">
                                        Stage {stage.stage_number}
                                    </span>
                                    <span className="text-gray-400 text-sm font-normal">
                                        {stage.challenges.length} challenge{stage.challenges.length !== 1 ? 's' : ''},
                                        {' '}{stage.rewards.length} reward{stage.rewards.length !== 1 ? 's' : ''}
                                    </span>
                                </h2>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Stage Dialogue */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wide">Dialogue</h3>
                                    <div className="grid gap-4 md:grid-cols-3">
                                        <div className="bg-slate-700/50 rounded-lg p-4">
                                            <h4 className="text-xs font-medium text-gray-400 mb-2">Pre-Stage</h4>
                                            {stage.dialogue_pre ? (
                                                <EditorRenderer content={stage.dialogue_pre} />
                                            ) : (
                                                <p className="text-gray-500 italic text-sm">No pre-stage dialogue</p>
                                            )}
                                        </div>
                                        <div className="bg-slate-700/50 rounded-lg p-4">
                                            <h4 className="text-xs font-medium text-green-400 mb-2">Success</h4>
                                            {stage.dialogue_success ? (
                                                <EditorRenderer content={stage.dialogue_success} />
                                            ) : (
                                                <p className="text-gray-500 italic text-sm">No success dialogue</p>
                                            )}
                                        </div>
                                        <div className="bg-slate-700/50 rounded-lg p-4">
                                            <h4 className="text-xs font-medium text-red-400 mb-2">Failure</h4>
                                            {stage.dialogue_failure ? (
                                                <EditorRenderer content={stage.dialogue_failure} />
                                            ) : (
                                                <p className="text-gray-500 italic text-sm">No failure dialogue</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Stage Challenges */}
                                <div>
                                    <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wide mb-3">
                                        Challenges ({stage.challenges.length})
                                    </h3>
                                    {stage.challenges.length === 0 ? (
                                        <p className="text-gray-500 text-sm">No challenges for this stage.</p>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {stage.challenges.map((challenge) => (
                                                <div key={challenge.id} className="flex items-center gap-2 px-3 py-2 bg-slate-700 rounded">
                                                    <span className="text-white text-sm">{allLabels[challenge.stat_type] || challenge.stat_type}</span>
                                                    <span className="text-gray-400 text-sm">({challenge.difficulty})</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Stage Rewards */}
                                <div>
                                    <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wide mb-3">
                                        Rewards ({stage.rewards.length})
                                    </h3>
                                    {stage.rewards.length === 0 ? (
                                        <p className="text-gray-500 text-sm">No rewards for this stage.</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {stage.rewards.map((reward) => (
                                                <RewardDisplay key={reward.id} reward={reward} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                )}
            </div>
        </GmLayout>
    );
}

/**
 * Reward display component.
 */
interface RewardDisplayProps {
    reward: {
        id: number;
        reward_type: { value: string; label: string; icon: string };
        item_recipe: { id: number; name: string } | null;
        item_rarity: { value: string; label: string } | null;
        material_type: string | null;
        enclave: { id: number; name: string; owner_name: string | null } | null;
        reputation_min: number | null;
        reputation_max: number | null;
        quantity_min: number;
        quantity_max: number;
        chance_percent: number;
        tags: { id: number; name: string }[];
    };
}

function RewardDisplay({ reward }: RewardDisplayProps) {
    // Build reward description
    // Get the reward type value for comparisons
    const rewardTypeValue = reward.reward_type.value;

    let description = '';
    if (rewardTypeValue === 'item') {
        if (reward.item_recipe) {
            description = `Item: ${reward.item_recipe.name}`;
        } else if (reward.item_rarity) {
            description = `Random ${reward.item_rarity.label} item`;
        } else {
            description = 'Random item';
        }
        if (reward.tags.length > 0) {
            description += ` (tags: ${reward.tags.map(t => t.name).join(', ')})`;
        }
    } else if (rewardTypeValue === 'blueprint') {
        if (reward.item_recipe) {
            description = `Blueprint: ${reward.item_recipe.name}`;
        } else if (reward.item_rarity) {
            description = `Random ${reward.item_rarity.label} blueprint`;
        } else {
            description = 'Random blueprint';
        }
        if (reward.tags.length > 0) {
            description += ` (tags: ${reward.tags.map(t => t.name).join(', ')})`;
        }
    } else if (rewardTypeValue === 'material') {
        description = `Material: ${reward.material_type || 'any'}`;
    } else if (rewardTypeValue === 'xp') {
        description = 'XP';
    } else if (rewardTypeValue === 'reputation') {
        const enclaveName = reward.enclave?.owner_name || reward.enclave?.name || 'Unknown';
        description = `Reputation with ${enclaveName}`;
    }

    // Quantity/Amount display
    let quantityDisplay = '';
    if (rewardTypeValue === 'reputation') {
        const formatRep = (val: number | null) => val !== null && val >= 0 ? `+${val}` : `${val}`;
        quantityDisplay = reward.reputation_min === reward.reputation_max
            ? formatRep(reward.reputation_min)
            : `${formatRep(reward.reputation_min)} to ${formatRep(reward.reputation_max)}`;
    } else if (rewardTypeValue === 'material' || rewardTypeValue === 'xp') {
        quantityDisplay = reward.quantity_min === reward.quantity_max
            ? `${reward.quantity_min}`
            : `${reward.quantity_min}-${reward.quantity_max}`;
    }

    return (
        <div className="flex items-center gap-4 p-3 bg-slate-700 rounded">
            <span className={`px-2 py-1 text-xs rounded ${
                rewardTypeValue === 'item' ? 'bg-purple-600 text-white' :
                rewardTypeValue === 'blueprint' ? 'bg-cyan-600 text-white' :
                rewardTypeValue === 'material' ? 'bg-amber-600 text-white' :
                rewardTypeValue === 'reputation' ? 'bg-indigo-600 text-white' :
                'bg-blue-600 text-white'
            }`}>
                {reward.reward_type.icon} {reward.reward_type.label}
            </span>
            <span className="text-white flex-1">{description}</span>
            {quantityDisplay && <span className="text-gray-400">{quantityDisplay}</span>}
            <span className="text-gray-400">{reward.chance_percent}% chance</span>
        </div>
    );
}

